import { useEffect, useRef, useState } from 'react'
import { css, Theme } from '@emotion/react'
import { BaseInput } from '@/components/input/BaseInput'
import { DestinationModal } from '../DestinationModal'
import { HealthInsurerFormData } from '@/types/healthInsurerFormData'
import { initialHealthInsurerForm } from '@/constants/initialHealthInsurerForm'
import { InsuranceTypeSelect } from '@/components/select/InsuranceTypeSelect'
import { showError, showSuccess } from '@/shared/toast'
import { useNavigate, useParams } from 'react-router-dom'
import { PrefectureSelect } from '@/components/select/PrefectureSelect'
import { useFetch } from '@/hooks/useFetch'
import {
  formatPostalCode,
  splitPostalCode,
  trimFull,
} from '@/utils/inputFormatter'
import { useHealthInsurerValidation } from '@/hooks/features/healthInsurer/useHealthInsurerValidation'
import { useSaveHealthInsurer } from '@/hooks/features/healthInsurer/useSaveHealthInsurer'
import { isFutureDate } from '@/utils/dateUtils'
import { isAxiosError } from 'axios'
import { BaseButton } from '@/components/button/BaseButton'
import { isDisabledAtom } from '@/store/state'
import { useAtom } from 'jotai'
import { HonorificSelect } from '@/components/select/HonorificSelect'
import { FormSection } from '@/components/form/FormSection'
import { FormFieldRow } from '@/components/form/FormFieldRow'
import { FormButtonArea } from '@/components/form/FormButtonArea'

// 白い枠のコンテナ
const whiteContainerStyle = css`
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 32px;
  width: 918px;
  max-width: 100%;
  margin-top: 24px;
  position: relative;
`

// 必須注記（右上）
const requiredNoteStyle = (t: Theme) => css`
  position: absolute;
  top: 32px;
  right: 32px;
  font-size: 1.4rem;
  color: ${t.c.ec.black};
`

// 必須マーク（赤色）
const requiredMarkStyle = css`
  color: #dc3545;
`

// フォームスタイル
const formStyle = css`
  display: flex;
  flex-direction: column;
`

// 適用開始日エリア（32px余白）
const startDateAreaStyle = css`
  margin-bottom: 32px;
`

// 郵便番号の横並びレイアウト
const postalCodeStyle = css`
  display: flex;
  gap: 0.8rem;
  align-items: center;
`

// 送達先フィールド（入力欄+ボタン）
const destinationFieldStyle = css`
  display: flex;
  gap: 4px;
  align-items: center;
`

// 送達先ボタンスタイル
const destinationButtonStyle = css`
  height: 26px;
  min-height: 26px;
  padding: 0 12px;
  font-size: 14px;
  line-height: 26px;
  box-sizing: border-box;
  border-radius: 8px;
`

// 2カラムレイアウト（保険者番号+保険者管理枝番、電話番号+FAX番号）
const twoColumnRowStyle = css`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-top: 8px;
`

const fieldColumnStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
`

// ラベル幅
const LABEL_WIDTH = 200

// フィールドサイズ定義
const fieldSizes = {
  startDate: css`
    width: 150px;
    height: 26px;
    font-size: 14px;
  `,
  insurerCode: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  branchCode: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  primaryInsurerCode: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  primaryInsurerCodeAcumoxi: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  name: css`
    width: 400px;
    height: 26px;
    font-size: 14px;
  `,
  insuranceType: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  prefecture: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  postalCode1: css`
    width: 80px;
    height: 26px;
    font-size: 14px;
  `,
  postalCode2: css`
    width: 100px;
    height: 26px;
    font-size: 14px;
  `,
  addressLine1: css`
    width: 650px;
    height: 26px;
    font-size: 14px;
  `,
  addressLine2: css`
    width: 650px;
    height: 26px;
    font-size: 14px;
  `,
  phoneNumber: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  faxNumber: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  honorific: css`
    width: 200px;
    height: 26px;
    font-size: 14px;
  `,
  insurerDestinationName: css`
    width: 400px;
    height: 26px;
    font-size: 14px;
  `,
  destinationName: css`
    width: 250px;
    height: 26px;
    font-size: 14px;
  `,
}

// セレクトボックスのカスタムスタイル（26px高さ）
const selectStyles = {
  control: (provided: any) => ({
    ...provided,
    height: '26px',
    minHeight: '26px',
    fontSize: '14px',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    height: '26px',
    padding: '0 8px',
  }),
  input: (provided: any) => ({
    ...provided,
    margin: '0',
    padding: '0',
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: '26px',
  }),
}

type Props = {
  onCancel: () => void
  onDirtyChange: (isDirty: boolean) => void
}

export const HealthInsurerForm = ({ onCancel, onDirtyChange }: Props) => {
  const { validate } = useHealthInsurerValidation()
  const { save } = useSaveHealthInsurer()
  const navigate = useNavigate()
  const id = useParams().id
  const startDateRef = useRef<string>('')
  const [isModalShow, setIsModalShow] = useState({
    show: false,
    kind: '1' as '1' | '2' | '3',
  })
  const [formData, setFormData] = useState<HealthInsurerFormData>(
    initialHealthInsurerForm,
  )
  const [initialFormData, setInitialFormData] = useState<HealthInsurerFormData>(
    initialHealthInsurerForm,
  )
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isDisabled, setIsDisabled] = useAtom(isDisabledAtom)
  const normalizeFormData = (data: HealthInsurerFormData): HealthInsurerFormData => {
    return Object.fromEntries(
      Object.entries(initialHealthInsurerForm).map(([key, initialValue]) => [
        key,
        data[key as keyof HealthInsurerFormData] ?? initialValue,
      ]),
    ) as HealthInsurerFormData
  }

  const { data, error } = useFetch<HealthInsurerFormData>(
    id ? `/api/health-insurers/${id}` : null,
  )

  useEffect(() => {
    if (id && data) {
      setIsDisabled(false)
      const normalizedData = normalizeFormData(data)
      const { postalCode1, postalCode2 } = splitPostalCode(normalizedData.postalCode)
      const nextFormData = {
        ...normalizedData,
        startDate: normalizedData.startDate.slice(0, 10),
        postalCode1,
        postalCode2,
      }
      startDateRef.current = normalizedData.startDate.slice(0, 10)
      if (!isFutureDate(normalizedData.startDate.slice(0, 10))) {
        setFormData((prev) => ({
          ...prev,
          startDate: '',
        }))
      }
      if (!isFutureDate(normalizedData.startDate.slice(0, 10))) {
        nextFormData.startDate = ''
      }
      setFormData(nextFormData)
      setInitialFormData(nextFormData)
      startDateRef.current = normalizedData.startDate.slice(0, 10)
    }
  }, [id, data])

  useEffect(() => {
    if (error) {
      showError('保険者情報の取得に失敗しました。')
    }
  }, [error])

  useEffect(() => {
    onDirtyChange(JSON.stringify(formData) !== JSON.stringify(initialFormData))
  }, [formData, initialFormData, onDirtyChange])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    setErrors((prev) => ({ ...prev, [name]: '' }))
    if ((name === 'postalCode1' || name === 'postalCode2') && value) {
      setErrors((prev) => ({ ...prev, postalCode: '' }))
    }
  }

  const trimFormData = (
    formData: HealthInsurerFormData,
  ): HealthInsurerFormData => {
    return {
      ...formData,
      name: trimFull(formData.name),
      addressLine1: trimFull(formData.addressLine1),
      addressLine2: trimFull(formData.addressLine2),
      insurerDestinationName: trimFull(formData.insurerDestinationName),
      insurerDestinationNameAcumoxi: trimFull(
        formData.insurerDestinationNameAcumoxi,
      ),
    }
  }

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setIsDisabled(true)

    const trimmedFormData = trimFormData(formData)

    const errors = validate(trimmedFormData)
    if (Object.keys(errors).length > 0) {
      setErrors(errors)
      setIsDisabled(false)
      return
    }
    const postalCode = formatPostalCode(
      trimmedFormData.postalCode1,
      trimmedFormData.postalCode2,
    )
    save(
      {
        ...trimmedFormData,
        postalCode: postalCode,
      },
      startDateRef.current,
      id,
    )
      .then((res: any) => {
        showSuccess(`保険者情報を${id ? '更新' : '登録'}しました。`)
        if (res.data.id) {
          navigate(`/health-insurers/${res.data.id}/view`)
        } else {
          navigate('/health-insurers')
        }
      })
      .catch((err) => {
        if (isAxiosError(err) && err.response?.status === 422) {
          const responseErrors = err.response.data.errors
          if (responseErrors.insurerCode) {
            setErrors((prev) => ({
              ...prev,
              insurerCode: responseErrors.insurerCode.join('\n'),
            }))
            return
          }
        }
        showError(`保険者情報の${id ? '更新' : '登録'}に失敗しました。`)
      })
      .finally(() => {
        setIsDisabled(false)
      })
  }

  return (
    <>
      {/* 白い枠のコンテナ */}
      <div css={whiteContainerStyle}>
        {/* 必須注記（右上） */}
        <div css={requiredNoteStyle}>
          <span css={requiredMarkStyle}>*</span>は必須項目です。
        </div>

        <form css={formStyle}>
          {/* 適用開始日（独立） */}
          <div css={startDateAreaStyle}>
            <FormFieldRow
              label="適用開始日"
              required
              error={errors.startDate}
              labelWidth={LABEL_WIDTH}
            >
              <BaseInput
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                addStyle={fieldSizes.startDate}
                min={new Date().toISOString().split('T')[0]}
                isError={!!errors.startDate}
              />
            </FormFieldRow>
          </div>

          {/* 1 基本情報 */}
          <FormSection title="1 基本情報" isFirst>
            {/* 保険者番号と保険者管理枝番を横並び */}
            <div css={twoColumnRowStyle}>
              <div css={fieldColumnStyle}>
                <FormFieldRow
                  label="保険者番号"
                  required
                  error={errors.insurerCode}
                  labelWidth={LABEL_WIDTH}
                >
                  <BaseInput
                    type="text"
                    name="insurerCode"
                    maxLength={8}
                    value={formData.insurerCode}
                    onChange={handleChange}
                    addStyle={fieldSizes.insurerCode}
                    isError={!!errors.insurerCode}
                  />
                </FormFieldRow>
              </div>

              <div css={fieldColumnStyle}>
                <FormFieldRow
                  label="保険者管理枝番"
                  error={errors.branchCode}
                  labelWidth={LABEL_WIDTH}
                  helpText="全国土木建築業健康保険組合を登録する場合に入力してください"
                >
                  <BaseInput
                    type="text"
                    name="branchCode"
                    maxLength={2}
                    value={formData.branchCode}
                    onChange={handleChange}
                    addStyle={fieldSizes.branchCode}
                    isError={!!errors.branchCode}
                  />
                </FormFieldRow>
              </div>
            </div>

            <FormFieldRow
              label="代表保険者番号"
              error={errors.primaryInsurerCode}
              labelWidth={LABEL_WIDTH}
            >
              <BaseInput
                type="text"
                name="primaryInsurerCode"
                maxLength={8}
                value={formData.primaryInsurerCode}
                onChange={handleChange}
                addStyle={fieldSizes.primaryInsurerCode}
                isError={!!errors.primaryInsurerCode}
              />
            </FormFieldRow>

            <FormFieldRow
              label="代表保険者番号（鍼灸）"
              error={errors.primaryInsurerCodeAcumoxi}
              labelWidth={LABEL_WIDTH}
            >
              <BaseInput
                type="text"
                name="primaryInsurerCodeAcumoxi"
                maxLength={8}
                value={formData.primaryInsurerCodeAcumoxi || ''}
                onChange={handleChange}
                addStyle={fieldSizes.primaryInsurerCodeAcumoxi}
                isError={!!errors.primaryInsurerCodeAcumoxi}
              />
            </FormFieldRow>

            <FormFieldRow
              label="保険者名"
              required
              error={errors.name}
              labelWidth={LABEL_WIDTH}
            >
              <BaseInput
                type="text"
                name="name"
                value={formData.name}
                maxLength={50}
                onChange={handleChange}
                addStyle={fieldSizes.name}
                isError={!!errors.name}
              />
            </FormFieldRow>

            <FormFieldRow
              label="保険種別"
              required
              error={errors.insuranceTypeCode}
              labelWidth={LABEL_WIDTH}
            >
              <InsuranceTypeSelect
                initialValue={formData.insuranceTypeCode}
                isSearchable={false}
                onChange={(newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    insuranceTypeCode: newValue?.value || '',
                  }))
                  setErrors((prev) => ({ ...prev, insuranceTypeCode: '' }))
                }}
                addStyle={fieldSizes.insuranceType}
                customStyles={selectStyles}
                isError={!!errors.insuranceTypeCode}
              />
            </FormFieldRow>

            <FormFieldRow
              label="都道府県"
              required
              error={errors.prefectureCode}
              labelWidth={LABEL_WIDTH}
            >
              <PrefectureSelect
                initialValue={formData.prefectureCode}
                isSearchable={false}
                onChange={(newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    prefectureCode: newValue?.value || '',
                  }))
                  setErrors((prev) => ({ ...prev, prefectureCode: '' }))
                }}
                addStyle={fieldSizes.prefecture}
                customStyles={selectStyles}
                isError={!!errors.prefectureCode}
              />
            </FormFieldRow>
          </FormSection>

          {/* 2 住所・連絡先 */}
          <FormSection title="2 住所・連絡先" showDivider>
            <FormFieldRow
              label="郵便番号"
              required
              error={errors.postalCode}
              labelWidth={LABEL_WIDTH}
            >
              <div css={postalCodeStyle}>
                <BaseInput
                  type="text"
                  name="postalCode1"
                  maxLength={3}
                  value={formData.postalCode1}
                  onChange={handleChange}
                  addStyle={fieldSizes.postalCode1}
                  isError={!!errors.postalCode}
                />
                <span>-</span>
                <BaseInput
                  type="text"
                  name="postalCode2"
                  maxLength={4}
                  value={formData.postalCode2}
                  onChange={handleChange}
                  addStyle={fieldSizes.postalCode2}
                  isError={!!errors.postalCode}
                />
              </div>
            </FormFieldRow>

            <FormFieldRow
              label="住所１"
              required
              error={errors.addressLine1}
              labelWidth={LABEL_WIDTH}
            >
              <BaseInput
                type="text"
                name="addressLine1"
                placeholder="英字、数字は半角で入力してください"
                value={formData.addressLine1}
                maxLength={50}
                onChange={handleChange}
                addStyle={fieldSizes.addressLine1}
                isError={!!errors.addressLine1}
              />
            </FormFieldRow>

            <FormFieldRow
              label="住所２"
              error={errors.addressLine2}
              labelWidth={LABEL_WIDTH}
              helpText={
                '番地・住所以降' +
                '\n' +
                '階数表記はFではなく、階で入力してください'
              }
            >
              <BaseInput
                type="text"
                name="addressLine2"
                placeholder="英字、数字は半角で入力してください"
                value={formData.addressLine2}
                maxLength={50}
                onChange={handleChange}
                addStyle={fieldSizes.addressLine2}
                isError={!!errors.addressLine2}
              />
            </FormFieldRow>

            {/* 電話番号とFAX番号を横並び */}
            <div css={twoColumnRowStyle}>
              <div css={fieldColumnStyle}>
                <FormFieldRow
                  label="電話番号"
                  error={errors.phoneNumber}
                  labelWidth={LABEL_WIDTH}
                >
                  <BaseInput
                    type="text"
                    name="phoneNumber"
                    placeholder="例: 03-1234-5678"
                    value={formData.phoneNumber}
                    maxLength={20}
                    onChange={handleChange}
                    addStyle={fieldSizes.phoneNumber}
                    isError={!!errors.phoneNumber}
                  />
                </FormFieldRow>
              </div>

              <div css={fieldColumnStyle}>
                <FormFieldRow
                  label="FAX番号"
                  error={errors.faxNumber}
                  labelWidth={LABEL_WIDTH}
                >
                  <BaseInput
                    type="text"
                    name="faxNumber"
                    placeholder="例: 03-1234-5678"
                    value={formData.faxNumber}
                    maxLength={20}
                    onChange={handleChange}
                    addStyle={fieldSizes.faxNumber}
                    isError={!!errors.faxNumber}
                  />
                </FormFieldRow>
              </div>
            </div>
          </FormSection>

          {/* 3 送達先情報 */}
          <FormSection title="3 送達先情報" showDivider>
            <FormFieldRow
              label="敬称"
              error={errors.honorific}
              labelWidth={LABEL_WIDTH}
            >
              <HonorificSelect
                initialValue={formData.honorific}
                onChange={(newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    honorific: newValue?.value || '',
                  }))
                }}
                isClearable={true}
                addStyle={fieldSizes.honorific}
                customStyles={selectStyles}
              />
            </FormFieldRow>

            <FormFieldRow
              label="保険者別送達先名"
              error={errors.insurerDestinationName}
              labelWidth={LABEL_WIDTH}
            >
              <BaseInput
                type="text"
                name="insurerDestinationName"
                value={formData.insurerDestinationName}
                maxLength={50}
                onChange={handleChange}
                addStyle={fieldSizes.insurerDestinationName}
                isError={!!errors.insurerDestinationName}
              />
            </FormFieldRow>

            <FormFieldRow
              label="送達先名"
              error={errors.destinationCode}
              labelWidth={LABEL_WIDTH}
            >
              <div css={destinationFieldStyle}>
                <BaseInput
                  type="text"
                  name="destinationName"
                  value={formData.destinationName}
                  readOnly={true}
                  placeholder="送達先を選択してください"
                  maxLength={50}
                  onChange={handleChange}
                  addStyle={fieldSizes.destinationName}
                />
                <BaseButton
                  addStyle={destinationButtonStyle}
                  onClick={() => {
                    setIsModalShow((prev) => ({
                      ...prev,
                      show: true,
                      kind: '1',
                    }))
                  }}
                >
                  選択
                </BaseButton>
                {formData.destinationCode && (
                  <BaseButton
                    addStyle={destinationButtonStyle}
                    type="button"
                    color="danger"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        destinationCode: '',
                        destinationName: '',
                      }))
                    }}
                  >
                    削除
                  </BaseButton>
                )}
              </div>
            </FormFieldRow>

            <FormFieldRow
              label="保険者別送達先名（鍼灸）"
              error={errors.insurerDestinationNameAcumoxi}
              labelWidth={LABEL_WIDTH}
            >
              <BaseInput
                type="text"
                name="insurerDestinationNameAcumoxi"
                value={formData.insurerDestinationNameAcumoxi || ''}
                maxLength={50}
                onChange={handleChange}
                addStyle={fieldSizes.insurerDestinationName}
                isError={!!errors.insurerDestinationNameAcumoxi}
              />
            </FormFieldRow>

            <FormFieldRow
              label="送達先名（鍼灸）"
              error={errors.destinationCodeAcumoxi}
              labelWidth={LABEL_WIDTH}
            >
              <div css={destinationFieldStyle}>
                <BaseInput
                  type="text"
                  name="destinationNameAcumoxi"
                  value={formData.destinationNameAcumoxi}
                  readOnly={true}
                  placeholder="送達先を選択してください"
                  maxLength={50}
                  onChange={handleChange}
                  addStyle={fieldSizes.destinationName}
                />
                <BaseButton
                  addStyle={destinationButtonStyle}
                  onClick={() => {
                    setIsModalShow((prev) => ({
                      ...prev,
                      show: true,
                      kind: '2',
                    }))
                  }}
                >
                  選択
                </BaseButton>
                {formData.destinationCodeAcumoxi && (
                  <BaseButton
                    addStyle={destinationButtonStyle}
                    type="button"
                    color="danger"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        destinationCodeAcumoxi: '',
                        destinationNameAcumoxi: '',
                      }))
                    }}
                  >
                    削除
                  </BaseButton>
                )}
              </div>
            </FormFieldRow>

            <FormFieldRow
              label="送達先名（受領委任）"
              error={errors.delegatedDestinationCode}
              labelWidth={LABEL_WIDTH}
            >
              <div css={destinationFieldStyle}>
                <BaseInput
                  type="text"
                  name="delegatedDestinationName"
                  value={formData.delegatedDestinationName}
                  readOnly={true}
                  placeholder="送達先を選択してください"
                  maxLength={50}
                  onChange={handleChange}
                  addStyle={fieldSizes.destinationName}
                />
                <BaseButton
                  addStyle={destinationButtonStyle}
                  onClick={() => {
                    setIsModalShow((prev) => ({
                      ...prev,
                      show: true,
                      kind: '3',
                    }))
                  }}
                >
                  選択
                </BaseButton>
                {formData.delegatedDestinationCode && (
                  <BaseButton
                    addStyle={destinationButtonStyle}
                    type="button"
                    color="danger"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        delegatedDestinationCode: '',
                        delegatedDestinationName: '',
                      }))
                    }}
                  >
                    削除
                  </BaseButton>
                )}
              </div>
            </FormFieldRow>
          </FormSection>
        </form>
      </div>

      {/* ボタンエリア */}
      <FormButtonArea
        cancelUrl="/health-insurers"
        onCancel={onCancel}
        onSubmit={handleSubmit}
        submitLabel={id ? '更新' : '登録'}
        isDisabled={isDisabled}
        showError={Object.values(errors).some(
          (error) => error && error.trim() !== '',
        )}
      />

      {isModalShow.show && (
        <DestinationModal
          onClose={function (): void {
            setIsModalShow((prev) => ({ ...prev, show: false }))
          }}
          onSelect={function (code: string, name: string): void {
            const keyForCode =
              isModalShow.kind === '1'
                ? 'destinationCode'
                : isModalShow.kind === '2'
                  ? 'destinationCodeAcumoxi'
                  : 'delegatedDestinationCode'
            const keyForName =
              isModalShow.kind === '1'
                ? 'destinationName'
                : isModalShow.kind === '2'
                  ? 'destinationNameAcumoxi'
                  : 'delegatedDestinationName'
            setFormData((prev) => ({
              ...prev,
              [keyForCode]: code,
              [keyForName]: name,
            }))
            setIsModalShow((prev) => ({ ...prev, show: false }))
          }}
          startDate={
            formData.startDate
              ? formData.startDate
              : new Date().toISOString().split('T')[0]
          }
        />
      )}
    </>
  )
}
